import { StageStepper } from "@dropbox/dash-component-library";
import { Button } from "@dropbox/dig-components/buttons";
import { Text, Title } from "@dropbox/dig-components/typography";
import { Box, Stack } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { CheckmarkCircleFill } from "@dropbox/dig-icons/dist/mjs/assets";
import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import useSettings from "@mirage/service-settings/useSettings";
import { settingsShortcutToFancyShortcut } from "@mirage/shared/hotkeys";
import i18n, { Trans } from "@mirage/translations";
import * as React from "react";
import { createPortal } from "react-dom";
import {
  DesktopBackgroundImage,
  LauncherAppImage,
  PersistentAppImage,
} from "./assets";
import styles from "./DualModeOnboarding.module.css";

import type { StageParams } from "@dropbox/dash-component-library";

const BlurbBulletPoint = ({ blurb }: { blurb: React.ReactNode }) => {
  return (
    <Box className={styles.bulletPoint}>
      <Box flexShrink={0} marginTop="4">
        <UIIcon
          src={CheckmarkCircleFill}
          color="var(--dig-color__identity__on-green)"
        />
      </Box>
      {blurb}
    </Box>
  );
};

const DesktopPreview = ({ previewImg }: { previewImg: React.ReactNode }) => {
  return (
    <Box className={styles.previewContainer}>
      <img
        src={DesktopBackgroundImage}
        alt={i18n.t("onboarding_dual_mode_preview_background_alt")}
        className={styles.previewContainerImage}
      />
      {previewImg}
    </Box>
  );
};

export const DualModeOnboarding: React.FC<{
  onComplete: () => void;
}> = ({ onComplete }) => {
  const { settings } = useSettings(["appShortcut"]);
  const isDarwin = EnvCtx.platform === "darwin";
  const launcherShortcut = settingsShortcutToFancyShortcut(
    isDarwin,
    settings?.appShortcut ?? "",
  ).join("");

  const steps: StageParams[] = [
    {
      body: (
        <Box style={{ marginTop: "92px" }}>
          <Title weightVariant="emphasized" className={styles.onboardingTitle}>
            {i18n.t("onboarding_dual_mode_stage_one_title")}
          </Title>
          <Box className={styles.bulletContainer}>
            <BlurbBulletPoint
              blurb={
                <Text size="xlarge" color="subtle">
                  {i18n.t("onboarding_dual_mode_stage_one_bullet_first")}
                </Text>
              }
            />
            <BlurbBulletPoint
              blurb={
                <Text size="xlarge" color="subtle">
                  <Trans
                    i18nKey="onboarding_dual_mode_stage_one_bullet_second"
                    components={{ bold: <b /> }}
                  />
                </Text>
              }
            />
            <BlurbBulletPoint
              blurb={
                <Text size="xlarge" color="subtle">
                  {i18n.t("onboarding_dual_mode_stage_one_bullet_third")}
                </Text>
              }
            />
          </Box>
        </Box>
      ),
      actions: [
        {
          element: <Button variant="transparent">{i18n.t("skip")}</Button>,
          type: "complete",
        },
        {
          element: (
            <Button
              variant="primary"
              size="xlarge"
              style={{
                width: "150px",
                borderRadius: "var(--dig-radius__circular)",
              }}
            >
              {i18n.t("continue_button")}
            </Button>
          ),
          type: "next",
        },
      ],
      split: {
        content: (
          <DesktopPreview
            previewImg={
              <Box className={styles.persistentAppImageContainer}>
                <img
                  src={PersistentAppImage}
                  className={styles.persistentAppImage}
                  alt={i18n.t(
                    "onboarding_dual_mode_preview_persistent_window_alt",
                  )}
                />
              </Box>
            }
          />
        ),
        actionSide: "left",
      },
    },
    {
      body: (
        <Stack style={{ marginTop: "92px" }}>
          <Title weightVariant="emphasized" className={styles.onboardingTitle}>
            {i18n.t("onboarding_dual_mode_stage_two_title", {
              shortcut: launcherShortcut,
            })}
          </Title>
          <Stack gap="24">
            <Box>
              <Text size="xlarge" color="subtle">
                {i18n.t("onboarding_dual_mode_stage_two_blurb", {
                  shortcut: launcherShortcut,
                })}
              </Text>
            </Box>
            <Box>
              <Text size="xlarge" color="subtle">
                {i18n.t("onboarding_dual_mode_stage_two_blurb_kb_settings")}
              </Text>
            </Box>
          </Stack>
        </Stack>
      ),
      actions: [
        {
          element: (
            <Button
              variant="primary"
              size="xlarge"
              style={{
                width: "150px",
                borderRadius: "var(--dig-radius__circular)",
              }}
            >
              {i18n.t("got_it")}
            </Button>
          ),
          type: "complete",
        },
      ],
      split: {
        content: (
          <DesktopPreview
            previewImg={
              <Box className={styles.launcherAppImageContainer}>
                <img
                  src={LauncherAppImage}
                  className={styles.launcherAppImage}
                  alt={i18n.t(
                    "onboarding_dual_mode_preview_launcher_window_alt",
                  )}
                />
              </Box>
            }
          />
        ),
        actionSide: "left",
      },
    },
  ];

  return createPortal(
    <Box className={styles.overlayContainer}>
      <StageStepper
        steps={steps}
        height="100%"
        onComplete={onComplete}
        showStepNumber={true}
        paddingBottom="52"
        paddingLeft="52"
        paddingRight="52"
        paddingTop="52"
      />
    </Box>,
    document.body,
  );
};
